import { checkIsGroupCharacter } from "@next-platform/client-common-core";
import { CharacterCardActions, CharacterCardVariant } from "@next-platform/client-common-spicy-ui";

import { Collection } from "@/types/api";
import { HitItem } from "@/types/hits";
import { getProfilePathForCharacter } from "@/utils";

import { CharacterCardDropdownActions } from "../types";
import getDropdownActions from "./getHitDropdownActions";

interface IGetHitCharacterConfigProps {
  character: HitItem;
  likedBots: string[];
  chatLanguage: string;
  dislikedRecommendedCharacterIds: string[];
  localizePathname: (path: string) => string;
  t: (key: string) => string;
  translateTag: (tag: string) => string;
  blurNSFW: boolean;
  isPartner: boolean;
  isAgeVerificationRequired: boolean;
  isAuthenticated: boolean;
  characterCardActionsHandler: (action: CharacterCardActions | CharacterCardDropdownActions, characterId: string, isNsfw?: boolean) => void;
  collection?: Collection;
}

/**
 * Returns the character hit config array based on provided parameters.
 */
export default function getHitCharacterConfig({
  character,
  likedBots,
  chatLanguage,
  dislikedRecommendedCharacterIds,
  localizePathname,
  t,
  translateTag,
  blurNSFW,
  isPartner,
  isAgeVerificationRequired,
  isAuthenticated,
  characterCardActionsHandler,
}: IGetHitCharacterConfigProps) {
  const characterId = character.character_id || character?.id;
  const isCharacterLiked = likedBots?.includes(characterId);

  const isGroupChat = checkIsGroupCharacter({ character });
  let infoLabelKey = "";

  if (character?.definition_visible) {
    infoLabelKey = isGroupChat ? t("chatbotCard:group.tooltip.definitionVisible") : t("chatbotCard:tooltip.definitionVisible");
  } else {
    infoLabelKey = isGroupChat ? t("chatbotCard:group.tooltip.definitionHidden") : t("chatbotCard:tooltip.definitionHidden");
  }

  return {
    id: characterId,
    isHidden: dislikedRecommendedCharacterIds.includes(characterId) && character?.recommId,
    variantLabel: character?.recommId ? t("chatbotCard:badge.forYou") : undefined,
    hasVoice: !!character?.voice_id,
    sub_characters_count: character?.sub_characters_count as number,
    characterAvatarBlur: blurNSFW && character?.avatar_is_nsfw,
    characterId,
    uniqueKey: character?.recommId ? `${characterId}-rec-${character?.recommId}` : characterId,
    characterName: character?.name_i18n?.[chatLanguage] || character?.name,
    characterDefinitionVisible: character?.definition_visible,
    characterCreatorUserName: character?.creator_username,
    characterTitle: character?.title_i18n?.[chatLanguage] || character?.title,
    characterTags: character?.tags?.map((tag: string) => translateTag(tag)),
    characterMessageNumber: character?.num_messages as number,
    characterRatingScore: character?.rating_score,
    characterTokensCount: character?.token_count,
    characterLikeLabel: isCharacterLiked ? t("chatbotCard:tooltip.unlike") : t("chatbotCard:tooltip.like"),
    characterInfoLabel: infoLabelKey,
    characterAvatarUrl: (character?.avatar_url as string) ?? "",
    characterImageGen: character?.lora_status === "ready" && !isPartner,
    characterImageGenLabel: t("chatbotCard:tooltip.imageGen"),
    characterCardVariant: character?.recommId ? CharacterCardVariant.RECOMMENDED : CharacterCardVariant.DEFAULT,
    creatorPagePath: localizePathname(`/creator/${character?.creator_username}`),
    chatPagePath: character?.is_nsfw && isAgeVerificationRequired ? undefined : localizePathname(`/chat/${characterId}`),
    chatbotProfilePagePath: localizePathname(getProfilePathForCharacter(character as HitItem)),
    characterActionsState: {
      [CharacterCardActions.FAVORITE]: {
        isDisabled: false,
        isHidden: !isAuthenticated,
        isLiked: isCharacterLiked,
      },
    },
    characterCardActionsHandler: (action: CharacterCardActions | CharacterCardDropdownActions, charId: string) => {
      characterCardActionsHandler(action, charId, character?.is_nsfw);
    },
    lorebookAttached: !!character?.has_lorebooks,
    lorebookLabel: character?.has_lorebooks ? t("chatbotCard:tooltip.lorebookAttached") : undefined,
    dropdownActions: getDropdownActions({
      t,
      characterCardActionsHandler,
      isAuthenticated,
      id: characterId,
    }),
  };
}
